import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["category", "budgetPreview", "totalBudget", "categoryList"]
  static values = { 
    totalBudget: Number,
    templatePercentages: Object,
    eventType: String
  }

  connect() {
    console.log("Category selector controller connected")
    // this.updateBudgetPreview()
    this.updateCategoryValidation()
  }

  toggleCategory(event) {
    const checkbox = event.target
    const category = checkbox.dataset.category
    const isEssential = checkbox.dataset.essential === "true"
    
    // Prevent unchecking essential categories if it would leave none selected
    if (!checkbox.checked && isEssential) {
      const essentialCategories = this.getSelectedEssentialCategories()
      if (essentialCategories.length === 1 && essentialCategories.includes(category)) {
        event.preventDefault()
        this.showValidationMessage("At least one essential category must be selected")
        return
      }
    }

    // Update visual feedback
    this.updateCategoryVisuals(checkbox)
    
    // Update budget preview
    // this.updateBudgetPreview()
    
    // Update validation status
    this.updateCategoryValidation()
    
    // Trigger custom event for other components
    this.dispatch("categoryChanged", { 
      detail: { 
        category: category, 
        selected: checkbox.checked,
        selectedCategories: this.getSelectedCategories()
      } 
    })
  }

  updateCategoryVisuals(checkbox) {
    const categoryRow = checkbox.closest('[data-category-row]')
    if (!categoryRow) return

    if (checkbox.checked) {
      categoryRow.classList.add('bg-blue-50', 'border-blue-200')
      categoryRow.classList.remove('bg-gray-50', 'border-gray-200')
    } else {
      categoryRow.classList.remove('bg-blue-50', 'border-blue-200')
      categoryRow.classList.add('bg-gray-50', 'border-gray-200')
    }

    // Update checkbox visual state
    const icon = categoryRow.querySelector('[data-category-icon]')
    if (icon) {
      if (checkbox.checked) {
        icon.classList.add('text-blue-600')
        icon.classList.remove('text-gray-400')
      } else {
        icon.classList.remove('text-blue-600')
        icon.classList.add('text-gray-400')
      }
    }
  }

  updateBudgetPreview() {
    if (!this.hasBudgetPreviewTarget || !this.totalBudgetValue) {
      return
    }

    const selectedCategories = this.getSelectedCategories()
    const budgetBreakdown = this.calculateBudgetBreakdown(selectedCategories)
    
    this.renderBudgetPreview(budgetBreakdown, selectedCategories)
    this.updateTotalBudgetDisplay()
  }

  calculateBudgetBreakdown(selectedCategories) {
    if (selectedCategories.length === 0) {
      return {}
    }

    const breakdown = {}
    const templatePercentages = this.templatePercentagesValue || {}
    
    // Calculate total percentage from template for selected categories
    let totalTemplatePercentage = 0
    selectedCategories.forEach(category => {
      totalTemplatePercentage += (templatePercentages[category] || 0)
    })

    // If no template percentages, distribute evenly
    if (totalTemplatePercentage === 0) {
      const evenPercentage = 100 / selectedCategories.length
      selectedCategories.forEach(category => {
        breakdown[category] = {
          percentage: evenPercentage,
          amount: (this.totalBudgetValue * evenPercentage / 100)
        }
      })
    } else {
      // Redistribute template percentages proportionally
      selectedCategories.forEach(category => {
        const templatePercentage = templatePercentages[category] || 0
        const adjustedPercentage = (templatePercentage / totalTemplatePercentage) * 100
        breakdown[category] = {
          percentage: adjustedPercentage,
          amount: (this.totalBudgetValue * adjustedPercentage / 100)
        }
      })
    }

    return breakdown
  }

  renderBudgetPreview(budgetBreakdown, selectedCategories) {
    if (!this.hasBudgetPreviewTarget) return

    const categories = Object.keys(budgetBreakdown)
    
    if (categories.length === 0) {
      this.budgetPreviewTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p class="text-sm">Select categories to see budget breakdown</p>
        </div>
      `
      return
    }

    const previewHTML = `
      <div class="space-y-3">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Budget Breakdown Preview</h4>
        ${categories.map(category => {
          const data = budgetBreakdown[category]
          return `
            <div class="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-md">
              <span class="text-sm font-medium text-gray-700">${category}</span>
              <div class="text-right">
                <span class="text-sm font-semibold text-gray-900">₱${this.formatCurrency(data.amount)}</span>
                <span class="text-xs text-gray-500 ml-1">(${data.percentage.toFixed(1)}%)</span>
              </div>
            </div>
          `
        }).join('')}
        <div class="border-t border-gray-200 pt-2 mt-3">
          <div class="flex justify-between items-center font-semibold">
            <span class="text-sm text-gray-900">Total Budget</span>
            <span class="text-sm text-gray-900">₱${this.formatCurrency(this.totalBudgetValue)}</span>
          </div>
        </div>
      </div>
    `

    this.budgetPreviewTarget.innerHTML = previewHTML
  }

  updateTotalBudgetDisplay() {
    if (!this.hasTotalBudgetTarget) return
    
    const selectedCount = this.getSelectedCategories().length
    
    this.totalBudgetTarget.textContent = selectedCount > 0 
      ? `Budget allocated across ${selectedCount} categories`
      : 'Select categories to allocate budget'
  }

  updateCategoryValidation() {
    const selectedEssential = this.getSelectedEssentialCategories()
    const isValid = selectedEssential.length > 0

    // Update form validation state
    const form = this.element.closest('form')
    if (form) {
      const submitButton = form.querySelector('input[type="submit"], button[type="submit"]')
      if (submitButton) {
        if (isValid) {
          submitButton.disabled = false
          submitButton.classList.remove('opacity-50', 'cursor-not-allowed')
        } else {
          submitButton.disabled = true
          submitButton.classList.add('opacity-50', 'cursor-not-allowed')
        }
      }
    }

    // Update validation message
    this.updateValidationMessage(isValid)
  }

  updateValidationMessage(isValid) {
    const validationContainer = this.element.querySelector('[data-validation-message]')
    if (!validationContainer) return

    if (isValid) {
      validationContainer.classList.add('hidden')
    } else {
      validationContainer.classList.remove('hidden')
      validationContainer.innerHTML = `
        <div class="flex items-center text-sm text-red-600">
          <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          Please select at least one essential category for your event.
        </div>
      `
    }
  }

  showValidationMessage(message) {
    // Create temporary validation message
    const tempMessage = document.createElement('div')
    tempMessage.className = 'mt-2 text-sm text-red-600 animate-pulse'
    tempMessage.textContent = message
    
    // Insert after the category that triggered it
    const lastCategoryRow = this.element.querySelector('[data-category-row]:last-child')
    if (lastCategoryRow) {
      lastCategoryRow.appendChild(tempMessage)
      
      // Remove after 3 seconds
      setTimeout(() => {
        tempMessage.remove()
      }, 3000)
    }
  }

  getSelectedCategories() {
    return Array.from(this.categoryTargets)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.dataset.category)
  }

  getSelectedEssentialCategories() {
    return Array.from(this.categoryTargets)
      .filter(checkbox => checkbox.checked && checkbox.dataset.essential === "true")
      .map(checkbox => checkbox.dataset.category)
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Handle budget input changes from the main form
  // budgetChanged(event) {
  //   const newBudget = parseFloat(event.target.value) || 0
  //   this.totalBudgetValue = newBudget
  //   this.updateBudgetPreview()
  // }

  // Handle event type changes that might affect essential categories
  eventTypeChanged(event) {
    const newEventType = event.target.value
    this.eventTypeValue = newEventType
    
    // Could trigger category template updates here
    this.dispatch("eventTypeChanged", { 
      detail: { 
        eventType: newEventType,
        selectedCategories: this.getSelectedCategories()
      } 
    })
  }

  // Method to programmatically update template percentages
  updateTemplatePercentages(newPercentages) {
    this.templatePercentagesValue = newPercentages
    // this.updateBudgetPreview()
  }

  // Reset to defaults based on event type
  resetToDefaults() {
    // Uncheck all categories first
    this.categoryTargets.forEach(checkbox => {
      checkbox.checked = false
      this.updateCategoryVisuals(checkbox)
    })

    // Check essential categories based on event type
    const essentialCategories = this.getEssentialCategoriesForEventType(this.eventTypeValue)
    essentialCategories.forEach(category => {
      const checkbox = this.categoryTargets.find(cb => cb.dataset.category === category)
      if (checkbox) {
        checkbox.checked = true
        this.updateCategoryVisuals(checkbox)
      }
    })

    // this.updateBudgetPreview()
    this.updateCategoryValidation()
  }

  getEssentialCategoriesForEventType(eventType) {
    const essentialByType = {
      'wedding': ['Venues', 'Catering', 'Photography'],
      'birthday': ['Venues', 'Catering', 'Entertainment'],
      'corporate': ['Venues', 'Catering', 'Lights & Sounds'],
      'default': ['Venues', 'Catering']
    }

    return essentialByType[eventType?.toLowerCase()] || essentialByType['default']
  }
}